import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
} from 'reactstrap';

import moment from 'moment';
import { withTranslation } from 'react-i18next';
import { compose } from 'recompose';
import ButtonWithIcon from '../button-with-icon';
import withAPI from '../../hocs/with-api';
import VehicleEventsInGeoZoneTable from './VehicleEventsInGeoZoneTable';

const iconStyle = {
  backgroundImage: 'url("/assets/info-icon.png")',
  backgroundSize: '20px',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
};


const ButtonVehiclesWithoutGeoZoneReportModal = ({
  t,
  order,
  onCancel,
  httpRequest,
}) => {
  const [isModalOpened, setIsModalOpened] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [checkResult, setCheckResult] = useState(null);

  const [isFetchingVehicleActivity, setIsFetchingVehicleActivity] = useState(false);
  const [vehicleActivityData, setVehicleActivityData] = useState(null);

  const [isFetchingAddressEvents, setIsFetchingAddressEvents] = useState(false);
  const [checkAddressEventsResult, setCheckAddressEventsResult] = useState(null);

  const toggle = () => {
    if (isModalOpened) {
      setCheckResult(null);
      setVehicleActivityData(null);
      setCheckAddressEventsResult(null);
    }

    setIsModalOpened(!isModalOpened);

    if (onCancel) {
      onCancel();
    }
  };

  const onCheckAddressIVMSIdClick = useCallback(async () => {
    const addressId = order?.trip?.client_address?.id;
    if (!addressId) return;

    setIsFetching(true);
    setCheckResult(null);

    try {
      const response = await httpRequest({
        method: 'get',
        url: `/check/address/navisat-ivms-id/${addressId}`,
      });

      const data = response?.data ?? response;

      if (!data) {
        setCheckResult({ className: 'text-danger', text: t('address.check_failed') });
        return;
      }

      if (data.success === false) {
        setCheckResult({
          className: 'text-danger',
          text: data.message || t('address.check_failed'),
        });
        return;
      }
      if (data.exists) {
        setCheckResult({
          className: 'text-success',
          text: t('address.exists_in_navisat'),
        });
      } else {
        setCheckResult({
          className: 'text-danger',
          text: t('address.not_exists_in_navisat'),
        });
      }
    } catch (e) {
      setCheckResult({
        className: 'text-danger',
        text: t('address.check_failed'),
      });
    } finally {
      setIsFetching(false);
    }
  }, [httpRequest, order, t]);


  const onGetLastNavisatActivityClick = useCallback(async () => {
    const vehicleId = order?.vehicle?.id;
    if (!vehicleId) return;

    setIsFetchingVehicleActivity(true);
    setVehicleActivityData(null);

    try {
      const response = await httpRequest({
        method: 'get',
        url: `/vehicles/get-last-navisat-activity/${vehicleId}`,
      });

      const data = response?.data ?? response;

      if (!data || data.success === false) {
        setVehicleActivityData({
          status: false,
          className: 'text-danger',
          text: t('vehicle.check_last_activity_failed'),
        });
        return;
      }

      if (data.success) {
        setVehicleActivityData({
          status: true,
          className: 'text-success',
          text: data.last_activity_at,
        });
      }
    } catch (e) {
      setVehicleActivityData({
        status: false,
        className: 'text-danger',
        text: t('vehicle.check_last_activity_failed'),
      });
    } finally {
      setIsFetchingVehicleActivity(false);
    }
  }, [httpRequest, order, t]);

  const onCheckAddressEventsClick = useCallback(async () => {
    setIsFetchingAddressEvents(true);
    setCheckAddressEventsResult(null);

    try {
      const response = await httpRequest({
        method: 'get',
        url: `/orders/get-geo-zone-activity/${order?.id}`,
      });

      const data = response?.data ?? response;
      setCheckAddressEventsResult(data);
    } catch (e) {
      setCheckAddressEventsResult(null);
    } finally {
      setIsFetchingAddressEvents(false);
    }
  }, [httpRequest, order]);

  const getModalTitle = () => {
    const title = `${t('details')}: ${t('delivery.address.end')}`;
    if (order?.number) {
      return `${title} (${order?.number})`;
    }

    return title;
  };


  return (
    <>
      <Button
        style={{
          display: 'block',
          margin: 'auto',
          width: '30px',
          height: '30px',
          backgroundColor: 'transparent',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          ...iconStyle,
        }}
        onClick={toggle}
      />
      <Modal isOpen={isModalOpened} toggle={toggle} centered size="xl">
        <ModalHeader toggle={toggle}>
          {getModalTitle()}
        </ModalHeader>
        <ModalBody>
          <div className="border p-4 rounded mb-2">
            <div className="mb-3">
              <span className="label-clean">
                {t('id.navisatGeozoneName')}
              </span>
              {': '}
              <span>
                {order?.trip?.client_address?.navisat_geozone_name
                  ? order.trip.client_address.navisat_geozone_name
                  : t('address.no_geozone_name')}
              </span>
            </div>

            {
              order.trip?.client_address?.navisat_geozone_name && (
                <>
                  <ButtonWithIcon
                    className="ml-0 btn-success"
                    onClick={onCheckAddressIVMSIdClick}
                    title={t('address.check_in_navisat')}
                    disabled={isFetching}
                  />
                  {checkResult && (
                    <p className={`${checkResult.className} mb-0`}>
                        {checkResult.text}
                    </p>
                  )}
                </>
              )
            }
          </div>
          {
            order.vehicle?.navisat_external_id && (
              <div className="border p-4 rounded">
                <div className="mb-3">
                  <span className="label-clean">
                    {t('vehicle')}
                  </span>
                  {': '}
                  <span>
                    {order?.vehicle?.number_plate}
                  </span>
                </div>
                <ButtonWithIcon
                  className="ml-0 btn-success w-auto"
                  onClick={onGetLastNavisatActivityClick}
                  title={t('vehicle.get_last_navisat_activity')}
                  disabled={isFetchingVehicleActivity}
                />
                {vehicleActivityData && (
                  <p className={`${vehicleActivityData.className} mb-0`}>
                    {vehicleActivityData.status
                      ? moment(vehicleActivityData.text).format('dddd, D MMMM YYYY HH:mm')
                      : vehicleActivityData.text}
                  </p>
                )}
              </div>
            )
          }

          {
            order.trip?.client_address?.navisat_geozone_name && order.vehicle?.navisat_external_id && (
            <div className="border p-4 rounded mt-2">
              <div className="mb-3">
                <span className="label-clean">
                  {t('text.checkInNavisatEventsInGeozone', { hours: 12 })}
                </span>
              </div>
              <ButtonWithIcon
                className="ml-0 btn-success"
                onClick={onCheckAddressEventsClick}
                title={t('address.check_in_navisat')}
                disabled={isFetchingAddressEvents}
              />
              {checkAddressEventsResult && (checkAddressEventsResult.length ? (
                <VehicleEventsInGeoZoneTable
                  data={checkAddressEventsResult}
                />
              ) : (
                <p className="text-danger mb-0">
                  {t('text.noNavisatEventsInGeozone', { hours: 12 })}
                </p>
              ))}
            </div>
           )
          }
        </ModalBody>
      </Modal>
    </>
  );
};

ButtonVehiclesWithoutGeoZoneReportModal.propTypes = {
  t: PropTypes.func.isRequired,
  order: PropTypes.object.isRequired,
  onCancel: PropTypes.func,
  httpRequest: PropTypes.func.isRequired,
};

ButtonVehiclesWithoutGeoZoneReportModal.defaultProps = {
  onCancel: null,
};

export default compose(
  withTranslation(),
  withAPI,
)(ButtonVehiclesWithoutGeoZoneReportModal);
